import React, { useState } from 'react';
import { Download, Calendar, BookOpen, Heart, Star, FileText } from 'lucide-react';
import CatholicApiService from '../services/catholicApi';

const Novenas = () => {
  const [selectedNovena, setSelectedNovena] = useState(null);
  const novenas = CatholicApiService.getNovenasForDownload();

  const additionalNovenas = [
    {
      id: 4,
      title: "Neuvaine à Sainte Thérèse de Lisieux",
      description: "Neuvaine à la petite Thérèse, docteur de l'Église et patronne des missions",
      downloadUrl: "https://paroissecremieu.fr/mediasdoc/2024/09/Neuvaine-Ste-Therese.pdf",
      duration: "9 jours",
      period: "Du 23 septembre au 1er octobre",
      theme: "Confiance et abandon à Dieu"
    },
    {
      id: 5,
      title: "Neuvaine de l'Immaculée Conception",
      description: "Neuvaine préparatoire à la fête de l'Immaculée Conception de la Vierge Marie",
      downloadUrl: "https://www.cathedraledumans.fr/wordpress/wp-content/uploads/2022/11/Neuvaine-Immacule%CC%81e-Conception.pdf",
      duration: "9 jours",
      period: "Du 30 novembre au 8 décembre",
      theme: "Pureté et grâce divine"
    },
    {
      id: 6,
      title: "Neuvaine de Noël",
      description: "Neuvaine de préparation à la Nativité de Notre-Seigneur Jésus-Christ",
      downloadUrl: "https://www.sodalitium.eu/wp-content/uploads/2024/12/Neuvaine-Noel-saint-Alphonse.pdf",
      duration: "9 jours",
      period: "Du 16 au 24 décembre",
      theme: "Incarnation et naissance du Sauveur"
    }
  ];

  const allNovenas = [...novenas, ...additionalNovenas];

  const handleDownload = (novena) => {
    // Simulation du téléchargement
    const link = document.createElement('a');
    link.href = novena.downloadUrl;
    link.download = `${novena.title.replace(/\s+/g, '_').toLowerCase()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Dans un vrai projet, on pourrait tracker les téléchargements
    console.log(`Téléchargement de: ${novena.title}`);
  };

  const getNovenaTips = () => [
    {
      icon: Calendar,
      title: "Régularité",
      description: "Priez chaque jour pendant 9 jours consécutifs, de préférence à la même heure"
    },
    {
      icon: Heart,
      title: "Intention",
      description: "Ayez une intention claire et priez avec foi et confiance"
    },
    {
      icon: BookOpen,
      title: "Méditation",
      description: "Prenez le temps de méditer sur les mystères proposés chaque jour"
    },
    {
      icon: Star,
      title: "Persévérance",
      description: "Même si vous ne voyez pas de résultat immédiat, persévérez dans la prière"
    }
  ];

  return (
    <section id="neuvaines" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Neuvaines téléchargeables
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Approfondissez votre vie spirituelle avec nos neuvaines traditionnelles. 
            Téléchargez gratuitement ces livrets de prière pour 9 jours de recueillement.
          </p>
        </div>

        {/* Grille des neuvaines */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {allNovenas.map((novena) => (
            <div key={novena.id} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <span className="bg-white px-3 py-1 rounded-full text-sm font-medium text-blue-600">
                    {novena.duration}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {novena.title}
                </h3>

                <p className="text-gray-600 mb-4 leading-relaxed">
                  {novena.description}
                </p>

                {novena.period && (
                  <div className="flex items-center space-x-2 text-sm text-blue-600 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span>{novena.period}</span>
                  </div>
                )}

                {novena.theme && (
                  <div className="flex items-center space-x-2 text-sm text-purple-600 mb-4">
                    <Star className="w-4 h-4" />
                    <span>Thème: {novena.theme}</span>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDownload(novena)}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Télécharger PDF</span>
                  </button>
                  <button
                    onClick={() => setSelectedNovena(novena)}
                    className="px-4 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Section explicative sur les neuvaines */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Qu'est-ce qu'une neuvaine ?</h3>
            <p className="text-purple-100 leading-relaxed">
              Une neuvaine est une pratique de dévotion catholique consistant à prier pendant 
              neuf jours consécutifs dans une intention particulière. Cette tradition puise 
              ses racines dans les neuf jours que les Apôtres et Marie ont passés en prière 
              entre l'Ascension et la Pentecôte.
            </p>
          </div>
        </div>

        {/* Conseils pour bien faire une neuvaine */}
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-800 text-center mb-8">
            Comment bien faire une neuvaine ?
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getNovenaTips().map((tip, index) => {
              const IconComponent = tip.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-800 mb-3">{tip.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Calendrier liturgique des neuvaines */}
        <div className="mt-16 bg-gray-50 rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-800 text-center mb-8">
            Calendrier des neuvaines liturgiques
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-blue-900">Cycle de l'Avent et Noël</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• Neuvaine de l'Immaculée Conception (30 nov - 8 déc)</li>
                <li>• Neuvaine de Noël (16-24 décembre)</li>
                <li>• Neuvaine de la Sainte Famille (25 déc - 2 jan)</li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-blue-900">Cycle pascal et temps ordinaire</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• Neuvaine au Sacré-Cœur (juin)</li>
                <li>• Neuvaine à Notre-Dame de Lourdes (2-11 février)</li>
                <li>• Neuvaine à saint Joseph (10-19 mars)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Modal pour prévisualisation (optionnel) */}
        {selectedNovena && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800">{selectedNovena.title}</h3>
                  <button
                    onClick={() => setSelectedNovena(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-600 mb-4">{selectedNovena.description}</p>
                  
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <h4 className="font-bold text-blue-900 mb-2">Aperçu de la neuvaine</h4>
                    <p className="text-sm text-blue-800">
                      Cette neuvaine comprend des prières quotidiennes, des méditations sur les mystères 
                      correspondants, et des intentions spécifiques pour chaque jour. Chaque journée 
                      propose une réflexion spirituelle approfondie pour nourrir votre âme.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => handleDownload(selectedNovena)}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Télécharger maintenant</span>
                  </button>
                  <button
                    onClick={() => setSelectedNovena(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Novenas;